import {Component, computed, input, InputSignal, output, OutputEmitterRef, Signal, viewChild} from '@angular/core';
import {DxDropDownBoxModule, DxTextBoxModule, DxTreeViewComponent, DxTreeViewModule} from 'devextreme-angular';
import type {ItemSelectionChangedEvent} from 'devextreme/ui/tree_view';
import type {ValueChangedEvent} from 'devextreme/ui/drop_down_box';
import {TreeNode} from './TreeNode';

@Component({
  selector: 'yo-multiselect',
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.scss'],
  standalone: true,
  imports: [DxDropDownBoxModule, DxTreeViewModule, DxTextBoxModule]
})
export class MultiselectComponent {

  readonly GROUP_PREFIX = '__group__';
  #initializing = false;

  data: InputSignal<any[]> = input<any[]>([]);
  displayValue: InputSignal<string> = input<string>('libelle');
  key: InputSignal<string> = input<string>('id');

  selectedItems: InputSignal<(string | number)[]> = input<(string | number)[]>([]);

  groupExpr: InputSignal<string | null> = input<string | null>(null);
  groupFn: InputSignal<((item: any) => string) | null> = input<((item: any) => string) | null>(null);
  groupLabel: InputSignal<((key: string) => string) | null> = input<((key: string) => string) | null>(null);

  placeholder: InputSignal<string> = input<string>('Sélectionner...');
  width: InputSignal<number | string | null> = input<number | string | null>(null);
  searchExpr: InputSignal<string | string[] | null> = input<string | string[] | null>(null);
  disabled: InputSignal<boolean> = input<boolean>(false);
  readOnly: InputSignal<boolean> = input<boolean>(false);
  expandedGroups: InputSignal<boolean> = input<boolean>(true);
  maxDisplayedTags: InputSignal<number | null> = input<number | null>(null);

  onValueChange: OutputEmitterRef<(string | number)[]> = output<(string | number)[]>();

  treeView: Signal<DxTreeViewComponent | undefined> = viewChild<DxTreeViewComponent>('treeView');

  treeData: Signal<TreeNode[]> = computed<TreeNode[]>(() => this.#buildTreeNodes());
  selectedItemObjects: Signal<any[]> = computed<any[]>(() => {
    const selected = this.selectedItems() ?? [];
    return this.data().filter(item => selected.includes(item[this.key()]));
  });
  visibleSelectedItemObjects: Signal<any[]> = computed<any[]>(() => {
    const max = this.maxDisplayedTags();
    return max === null ? this.selectedItemObjects() : this.selectedItemObjects().slice(0, max);
  });
  hiddenCount: Signal<number> = computed<number>(() => {
    const max = this.maxDisplayedTags();
    return this.maxDisplayedTags() === null ? 0 : Math.max(0, this.selectedItemObjects().length - max);
  });

  /** Point d'entrée : déduplique les items puis délègue à la construction groupée ou plate. */
  #buildTreeNodes(): TreeNode[] {
    const uniqueData = this.#deduplicateByKey(this.data());
    return (this.groupFn() || this.groupExpr())
      ? this.#buildGroupedNodes(uniqueData)
      : this.#buildFlatNodes(uniqueData);
  }

  /** Filtre les doublons en se basant sur la clé de chaque item. */
  #deduplicateByKey(items: any[]): any[] {
    const seenKeys = new Set<string | number>();
    return items.filter(item => {
      const k = item[this.key()] as string | number;
      if (seenKeys.has(k)) return false;
      seenKeys.add(k);
      return true;
    });
  }

  /** Construit les nœuds groupés : un nœud parent par groupe suivi de ses enfants. */
  #buildGroupedNodes(uniqueData: any[]): TreeNode[] {
    const groupMap = this.#buildGroupMap(uniqueData);
    const nodes: TreeNode[] = [];
    for (const [groupKey, items] of groupMap) {
      const groupNodeId = `${this.GROUP_PREFIX}${groupKey}`;
      const groupName: string = this.groupLabel() ? this.groupLabel()(groupKey) : groupKey;
      nodes.push({__treeId: groupNodeId, __parentId: null, [this.displayValue()]: groupName, expanded: this.expandedGroups()});
      for (const item of items) {
        nodes.push({...item, __treeId: item[this.key()] as string | number, __parentId: groupNodeId});
      }
    }
    return nodes;
  }

  /** Regroupe les items par clé de groupe (via groupFn ou groupExpr). */
  #buildGroupMap(uniqueData: any[]): Map<string, any[]> {
    const groupMap = new Map<string, any[]>();
    for (const item of uniqueData) {
      const groupKey: string = this.groupFn() ? this.groupFn()(item) : item[this.groupExpr()] as string;
      if (!groupMap.has(groupKey)) groupMap.set(groupKey, []);
      groupMap.get(groupKey).push(item);
    }
    return groupMap;
  }

  /** Construit une liste plate de nœuds sans groupement. */
  #buildFlatNodes(uniqueData: any[]): TreeNode[] {
    return uniqueData.map(item => ({...item, __treeId: item[this.key()] as string | number, __parentId: null}));
  }

  onTreeContentReady(e: any): void {
    if (this.selectedItems()?.length) {
      this.#initializing = true;
      this.selectedItems().forEach(id => e.component.selectItem(id));
      this.#initializing = false;
    }
  }

  onNodeSelectionChanged(e: ItemSelectionChangedEvent): void {
    if (this.#initializing) return;

    const changedKey = e.itemData.__treeId as string | number;
    const isSelected = e.node.selected;
    const current = new Set(this.selectedItems() ?? []);

    if (String(changedKey).startsWith(this.GROUP_PREFIX)) {
      const childKeys = this.treeData()
        .filter(n => n.__parentId === changedKey)
        .map(n => n.__treeId as string | number);
      childKeys.forEach(k => isSelected ? current.add(k) : current.delete(k));
    } else {
      isSelected ? current.add(changedKey) : current.delete(changedKey);
    }

    this.onValueChange.emit([...current]);
  }

  onInputCleared(e: ValueChangedEvent): void {
    if (e.value === null || e.value === '') {
      this.onValueChange.emit([]);
    }
  }

  removeItem(event: Event, itemKey: string | number): void {
    event.stopPropagation();
    if (this.treeView()?.instance) {
      this.#initializing = true;
      this.treeView().instance.unselectItem(itemKey);
      this.#initializing = false;
    }
    const newKeys = this.selectedItems().filter(k => k !== itemKey);
    this.onValueChange.emit(newKeys);
  }
}
